import jsPDF from "jspdf";
import "jspdf-autotable";
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"
import { getVendedoresTexto } from "./helpers/vendedoresPdf.js"

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatPercentage = (value) => `${Number(value || 0).toFixed(1)}%`;

const buildFiltrosTexto = ({ viajeDesde, viajeHasta, fechaInicio, grupo, destino, estado, orden }) => {
  const filtros = [];

  if (viajeDesde || viajeHasta) {
    filtros.push(`Viaje: ${viajeDesde || "N/A"} - ${viajeHasta || "N/A"}`);
  }
  if (fechaInicio) filtros.push(`Fecha inicio exacta: ${fechaInicio}`);
  if (grupo) filtros.push(`Grupo: ${grupo}`);
  if (destino) filtros.push(`Destino: ${destino}`);
  if (estado) filtros.push(`Estado: ${estado}`);
  if (orden) filtros.push(`Orden: ${orden}`);

  return filtros.length ? filtros : ["Filtros: Todos"];
};

export const generarPdfVerBalance = ({ rows = [], filtros = {}, usuario = "Usuario", logoBase64 = null, deliveryContext = null }) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
  };

  const totalPrecioOriginal = rows.reduce((sum, row) => sum + (Number(row?.precioOriginal) || 0), 0);
  const totalPrecioTotal = rows.reduce((sum, row) => sum + (Number(row?.precioPorPasajeros) || 0), 0);
  const totalCostoTotal = rows.reduce((sum, row) => sum + (Number(row?.costoTotalServicios) || 0), 0);
  const totalGanancia = rows.reduce((sum, row) => sum + (Number(row?.ganancia) || 0), 0);

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let yPosition = 36;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...PAL.primary);
  doc.text("REPORTE VER BALANCE", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.inkLight);
  doc.text(
    `Fecha de generacion: ${new Date().toLocaleString("es-AR")} | Usuario: ${usuario}`,
    pageWidth - margin,
    yPosition,
    { align: "right" }
  );
  yPosition += 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...PAL.ink);
  doc.text("Filtros aplicados:", margin, yPosition);
  yPosition += 14;

  doc.setDrawColor(...PAL.primary);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 12;

  const filtrosTexto = buildFiltrosTexto(filtros);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.muted);
  doc.text(filtrosTexto.join(" | "), margin, yPosition, {
    maxWidth: pageWidth - margin * 2,
  });
  yPosition += 24;

  doc.setFillColor(...PAL.bgSoft);
  doc.roundedRect(margin, yPosition - 13, 760, 20, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...PAL.ink);
  doc.text(
    `Registros: ${rows.length} | Precio Original: ${formatCurrency(totalPrecioOriginal)} | Precio Total: ${formatCurrency(totalPrecioTotal)} | Costo Total: ${formatCurrency(totalCostoTotal)} | Ganancia: ${formatCurrency(totalGanancia)}`,
    margin + 8,
    yPosition
  );
  yPosition += 24;

  const head = [
    "Fecha Inicio",
    "Grupo",
    "Destino",
    "Vendedor",
    "Precio Original",
    "Precio Total",
    "Costo Total",
    "Ganancia",
    "% Ganancia",
  ];

  const body = rows.map((row) => [
    row?.fechaInicio || "N/A",
    row?.codigoProductoTerminado || "",
    row?.destino || "",
    getVendedoresTexto(row),
    formatCurrency(row?.precioOriginal),
    formatCurrency(row?.precioPorPasajeros),
    formatCurrency(row?.costoTotalServicios),
    formatCurrency(row?.ganancia),
    formatPercentage(row?.porcentajeGanancia),
  ]);

  doc.autoTable({
    startY: yPosition,
    head: [head],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { cellWidth: 70, halign: "center" },
      1: { cellWidth: 110 },
      2: { cellWidth: 110 },
      3: { cellWidth: 90 },
      4: { cellWidth: 85, halign: "right" },
      5: { cellWidth: 85, halign: "right" },
      6: { cellWidth: 85, halign: "right" },
      7: { cellWidth: 85, halign: "right" },
      8: { cellWidth: 60, halign: "right" },
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...PAL.inkLight);
      const pageText = `Pagina ${data.pageNumber} de ${doc.internal.pages.length - 1}`;
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" });
    },
  });

  const fileName = `ver_balance_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.setProperties({ title: fileName, subject: "Reporte Ver Balance", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true;
};
